import {skipToken, useQuery, UseQueryOptions} from '@tanstack/react-query';
import {MethodId, MethodType} from '~/entities/operations-fiat';
import {expectEither, HttpClient, POST, useHttpClient} from '~/library/network-facade';
import {AccountId, Outcome} from '~/library/types';
import {useShallowDebounce} from '~/library/ui-kit';
import {FeeData} from '../../model/FeeData';

async function fetchTopupPaymentNetworkFee(
    http: HttpClient,
    accountId: AccountId,
    methodId: MethodId | undefined,
    amount: string
) {
    return http(
        `/v1/accounts/${accountId}/receive/banks/${methodId}/fee`,
        POST({
            amount,
        })
    ).then(
        ...expectEither({
            200: FeeData,
        })
    );
}

async function fetchTopupCardFee(
    http: HttpClient,
    accountId: AccountId,
    methodId: MethodId | undefined,
    amount: string
) {
    return http(
        `/v1/accounts/${accountId}/receive/cards/${methodId}/fee`,
        POST({
            amount,
        })
    ).then(
        ...expectEither({
            200: FeeData,
        })
    );
}

type TopupFeeInput = {
    accountId: AccountId;
    methodType: MethodType | undefined;
    methodId: MethodId | undefined;
    amount: string;
};

export function useTopupFeeOptions({
    accountId,
    methodType,
    methodId,
    amount,
}: TopupFeeInput): UseQueryOptions<Outcome<FeeData | null, never>> {
    const http = useHttpClient();

    return {
        queryKey: [
            'v1/accounts/{accountId}/receive/{methodType}/{methodId}/fee',
            accountId,
            methodType,
            methodId,
            amount,
        ],
        queryFn:
            !methodType || !methodId
                ? skipToken
                : async ({queryKey, client}) => {
                      const requestFunction = (() => {
                          switch (methodType) {
                              case 'card':
                                  return fetchTopupCardFee;
                              case 'payment_network':
                                  return fetchTopupPaymentNetworkFee;
                          }
                      })();

                      const res = await requestFunction(http, accountId, methodId, amount);

                      switch (res.status) {
                          case 200:
                              return {value: res.json};
                          case 'Abort': {
                              client.cancelQueries({queryKey});
                              throw res.reason;
                          }
                      }
                  },
        enabled: !!methodType && !!methodId,
    };
}

export function useDebouncedTopupFee(input: TopupFeeInput) {
    const {isDebouncing, value} = useShallowDebounce(200, input);
    const queryOptions = useTopupFeeOptions(value);
    const query = useQuery(queryOptions);
    return {...query, isDebouncing};
}
